import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService, User } from '../../core/services/auth.service';
import { VersionService } from '../../core/services/version.service';
import { ThemeService, Theme } from '../../core/services/theme.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BugReportDialogComponent } from '../components/bug-report-dialog/bug-report-dialog.component';
import { BugReportService } from '../../core/services/bug-report.service';
import { Announcement, AnnouncementService } from '../../core/services/announcement.service';
import { PatchNotesSeenService } from '../../core/services/patch-notes-seen.service';
// Type-only import: keeps the dialog (and its marked + DOMPurify dependencies) OUT of the eager app-shell
// bundle. The component itself is dynamically imported in openAlerts() so it lands in a lazy chunk.
import type { PatchNotesDialogData } from '../components/patch-notes-dialog/patch-notes-dialog.component';

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSidenavModule,
    MatListModule,
    MatTooltipModule
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss'
})
export class NavigationComponent implements OnInit, OnDestroy {
  isMenuOpen = false;
  /** Drawer email is blurred by default (privacy on shared screens); click to reveal. */
  emailRevealed = false;
  currentUser$: Observable<User | null>;
  hasBugReport$: Observable<boolean>;
  isAdmin$: Observable<boolean>;
  isAuthenticated = false;
  private destroy$ = new Subject<void>();

  private readonly ALERTS_LIMIT = 5;
  /** Recent published announcements, loaded once. Backs the bezel "Alerts" button + dialog. */
  private alerts: Announcement[] = [];
  /** True when there are unread alerts — illuminates the bezel button. */
  readonly hasUnreadAlerts = signal(false);

  constructor(
    private authService: AuthService,
    private versionService: VersionService,
    private dialog: MatDialog,
    private bugReportService: BugReportService,
    private themeService: ThemeService,
    private announcementService: AnnouncementService,
    private seenService: PatchNotesSeenService
  ) {
    this.currentUser$ = this.authService.currentUser$;
    this.hasBugReport$ = this.bugReportService.hasBugReport$;
    this.isAdmin$ = this.authService.isAdmin$;
  }

  get theme() {
    return this.themeService.theme;
  }

  setTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
  }

  ngOnInit(): void {
    this.isAuthenticated = this.authService.isAuthenticated();
    this.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.isAuthenticated = !!user;
      });
    this.versionService.loadVersion();
    this.loadAlerts();
  }

  private loadAlerts(): void {
    this.announcementService
      .getRecent(this.ALERTS_LIMIT)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: entries => {
          this.alerts = entries;
          this.hasUnreadAlerts.set(this.seenService.hasUnseen(entries));
        },
        // Best-effort: a failed feed shows a plain (read) Alerts button.
        error: () => {
          this.alerts = [];
          this.hasUnreadAlerts.set(false);
        }
      });
  }

  async openAlerts(): Promise<void> {
    const entries = this.alerts;

    // Opening marks the currently-shown entries as read and dims the button.
    this.seenService.markSeen(entries);
    this.hasUnreadAlerts.set(false);

    // Lazy-load the dialog so marked + DOMPurify stay out of the initial bundle.
    const { PatchNotesDialogComponent } = await import(
      '../components/patch-notes-dialog/patch-notes-dialog.component'
    );

    this.dialog.open(PatchNotesDialogComponent, {
      data: { entries } satisfies PatchNotesDialogData,
      width: '640px',
      maxWidth: '95vw',
      autoFocus: false,
      restoreFocus: true
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get version$() {
    return this.versionService.version$;
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
    // Re-blur the email whenever the drawer closes (or on logout) so it never
    // stays revealed for the next open — keeps the "blurred by default" intent.
    this.emailRevealed = false;
  }

  toggleEmailReveal(): void {
    this.emailRevealed = !this.emailRevealed;
  }

  logout(): void {
    this.authService.logout();
    this.closeMenu();
  }

  openBugReportDialog(): void {
    if (!this.isAuthenticated) {
      return;
    }

    this.closeMenu();
    this.dialog.open(BugReportDialogComponent, {
      width: 'min(480px, 90vw)',
      maxWidth: '480px'
    });
  }
}
